import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE, page, cardScroll, inp, btn } from './AdminUsers';
import TopBanner from '../components/TopBanner';
import AdminSidebar from '../components/AdminSidebar';

// Phase 1 of a user-requested feature ("active sessions of all users of
// all apps to monitor"). Read-only view over Authentik's own live
// session state, via backend/src/routes/adminActiveSessions.js.
//
// ADDED (2026-10-16), Phase 2: a per-app "active in" column, sourced from
// tabPresence.js's own per-app heartbeat data (see that lib's and
// adminActiveSessions.js's own header comments) — independent of
// Authentik's own session object, which still carries no app/provider
// field at all. An app whose own heartbeat sender hasn't been updated to
// send its slug yet shows up as "Unknown app" rather than being silently
// dropped (see APP_DISPLAY_NAMES's own fallback below).
//
// Not paginated server-side like AdminAuditLog — the dataset here is
// bounded to "currently logged-in people right now," never thousands of
// rows the way historical events are, so a plain client-side filter is
// enough.

// Keep in sync with whatever slug each app's own tab-presence sender
// actually passes (see tabPresence.js's routes for the 'sso-portal'
// hardcoded case, and each connected app's own tab_presence.js/
// session_check.py for the rest) — 'transfer'/'md-dashboard'/
// 'officers-directory' are also the EXACT slugs already used by
// backchannelLogoutShared.js's own app registry (bescom:app-registry:*),
// reused here for consistency rather than inventing separate ones.
const APP_DISPLAY_NAMES = {
  'sso-portal': 'SSO Portal',
  'transfer': 'Transfer',
  'md-dashboard': 'MD Dashboard',
  'officers-directory': 'Contact Directory',
  'bicc-dashboard': 'BICC Dashboard',
  'coi-plan': 'Samvidhan',
  'unknown': 'Unknown app',
};
function appDisplayName(slug) {
  return APP_DISPLAY_NAMES[slug] || slug;
}

const thStyle = {
  textAlign: 'left', padding: '10px 12px', fontSize: '12px', fontWeight: '700',
  color: '#666', textTransform: 'uppercase', letterSpacing: '0.04em',
  borderBottom: '2px solid #eee', background: '#fafafa',
};
const tdStyle = { padding: '10px 12px', fontSize: '13px', color: '#2c2c2a', borderBottom: '1px solid #f0f0f0' };

function formatDuration(ms) {
  if (!ms || ms < 0) return '—';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (hours) return `${hours}h ${minutes}m`;
  if (minutes) return `${minutes}m`;
  return `${totalSeconds}s`;
}

export default function AdminActiveSessions() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [sessions, setSessions] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  // Same two-case guard every other admin page in this sidebar uses —
  // checking the group only when `user` is already truthy would
  // silently do nothing for a visitor with no session at all.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/');
      return;
    }
    const groups = user.profile?.groups || [];
    if (!groups.includes('bescom-super-admins')) {
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate]);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/active-sessions`, { credentials: 'include' });
      if (res.status === 401) {
        navigate('/', { state: { sessionExpired: true } });
        return;
      }
      if (res.status === 403) {
        setError('You are not authorized to view active sessions.');
        return;
      }
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not load active sessions.');
        return;
      }
      setSessions(data.sessions || []);
      setLastFetched(new Date());
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const now = Date.now();
  const filtered = sessions.filter((s) => {
    if (!filter) return true;
    const needle = filter.toLowerCase();
    return (s.username || '').toLowerCase().includes(needle)
      || (s.name || '').toLowerCase().includes(needle)
      || (s.email || '').toLowerCase().includes(needle)
      || (s.lastIp || '').includes(needle);
  });

  return (
    <>
      <TopBanner />
      <div className="admin-page-wrap" style={page}>
        <div className="admin-row" style={{ display: 'flex', gap: '24px', width: '100%', height: '100%' }}>
          <AdminSidebar active="active-sessions" />
          <div style={cardScroll}>
            <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 4px' }}>
              Active Sessions
            </h1>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '24px' }}>
              Everyone currently logged in, right now, across the whole SSO ecosystem — sourced directly from
              Authentik's own live session state, plus a live per-app breakdown (which connected apps each
              person currently has open, and since when) sourced from each app's own tab-presence heartbeats.
            </p>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                style={{ ...inp, maxWidth: '280px' }}
                placeholder="Filter by name, username, email, or IP…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
              <button type="button" onClick={fetchSessions} style={{ ...btn, padding: '8px 16px', fontSize: '13px' }}>
                ↻ Refresh
              </button>
              {lastFetched && (
                <span style={{ fontSize: '12px', color: '#888' }}>
                  Last updated {lastFetched.toLocaleTimeString()}
                </span>
              )}
            </div>

            {error && (
              <div style={{ background: '#FDECEC', color: '#B3261E', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' }}>
                {error}
              </div>
            )}

            {loading ? (
              <p style={{ textAlign: 'center', color: '#666', fontSize: '14px' }}>Loading…</p>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
                <p style={{ fontSize: '32px', margin: '0 0 16px' }}>👤</p>
                <p>{sessions.length === 0 ? 'No one is currently logged in.' : 'No sessions match that filter.'}</p>
              </div>
            ) : (
              <>
                <div style={{ border: '1px solid #eee', borderRadius: '10px', overflow: 'hidden', overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '920px' }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>User</th>
                        <th style={thStyle}>Active in</th>
                        <th style={thStyle}>IP</th>
                        <th style={thStyle}>Browser / OS</th>
                        <th style={thStyle}>Session duration</th>
                        <th style={thStyle}>Last activity</th>
                        <th style={thStyle}>Expires</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((s) => (
                        <tr key={s.sessionId}>
                          <td style={{ ...tdStyle, fontWeight: '600' }}>
                            {s.userPk ? (
                              <Link
                                to={`/admin/users/${s.userPk}`}
                                draggable={false}
                                style={{ color: '#0072B1', cursor: 'pointer', textDecoration: 'none', WebkitUserDrag: 'none', userSelect: 'text' }}
                              >
                                {s.username || s.email || `(pk ${s.userPk})`}
                              </Link>
                            ) : (s.username || '—')}
                            {s.isActive === false && (
                              <span style={{ marginLeft: '8px', fontSize: '11px', fontWeight: '700', color: '#B3261E', background: '#FDECEC', padding: '2px 8px', borderRadius: '10px' }}>
                                deactivated
                              </span>
                            )}
                            {s.name && (
                              <div style={{ fontWeight: '400', fontSize: '12px', color: '#888' }}>{s.name}</div>
                            )}
                          </td>
                          <td style={tdStyle}>
                            {(s.apps || []).length === 0 ? (
                              <span style={{ color: '#aaa' }}>—</span>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                {s.apps.map((a) => (
                                  <span
                                    key={a.app}
                                    title={a.live ? 'Currently active' : 'No recent heartbeat — likely a closed tab not yet swept'}
                                    style={{
                                      display: 'inline-flex', alignItems: 'center', gap: '5px',
                                      fontSize: '11px', fontWeight: '600', padding: '2px 8px',
                                      borderRadius: '10px', width: 'fit-content',
                                      color: a.live ? '#0F6E56' : '#888',
                                      background: a.live ? '#E9F7EF' : '#f2f2f2',
                                    }}
                                  >
                                    {appDisplayName(a.app)} · open {formatDuration(now - a.firstSeen)}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td style={{ ...tdStyle, color: '#666', fontFamily: 'monospace', fontSize: '12px' }}>
                            {s.lastIp || '—'}
                          </td>
                          <td style={{ ...tdStyle, color: '#666' }}>
                            {[s.browser, s.os].filter((v) => v && v !== 'Other').join(' / ') || '—'}
                          </td>
                          <td style={tdStyle}>
                            {s.loginAt ? formatDuration(now - s.loginAt) : '—'}
                          </td>
                          <td style={{ ...tdStyle, whiteSpace: 'nowrap', color: '#666' }}>
                            {s.lastUsed ? new Date(s.lastUsed).toLocaleString() : '—'}
                          </td>
                          <td style={{ ...tdStyle, whiteSpace: 'nowrap', color: '#666' }}>
                            {s.expires ? new Date(s.expires).toLocaleString() : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: '16px', fontSize: '12px', color: '#888' }}>
                  {filtered.length} of {sessions.length} active session{sessions.length === 1 ? '' : 's'}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
